import { Button, Menu, MenuItem } from '@mui/material'

import { useTranslate } from '@tolgee/react'
import { MRT_RowData, MRT_TableInstance } from 'material-react-table'
import { useState } from 'react'
import { useMinimalsTheme } from '../../../../hooks'
import { ChevronDownIcon } from '../../../icons'
import {
  IDataExportConfig,
  exportTableToCsv,
  exportTableToXlsx,
  generateFileNameWithDate,
} from '../utils/xlsxUtils'

interface IProps<T extends MRT_RowData> {
  workbookTitle: string
  table: MRT_TableInstance<T>
  config?: IDataExportConfig<T>

  customExportCsv?: (table: MRT_TableInstance<T>) => void
  customExportXlsx?: (table: MRT_TableInstance<T>) => void
}

export const TomTableExportButton = <T extends MRT_RowData>({
  table,
  workbookTitle,
  config,
  customExportCsv,
  customExportXlsx,
}: IProps<T>) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const handleXlsxExport = () => {
    if (customExportXlsx) {
      customExportXlsx(table)
    } else {
      exportTableToXlsx(
        workbookTitle,
        table,
        config,
        generateFileNameWithDate(workbookTitle, 'xlsx'),
      )
    }
    closeMenu()
  }

  const handleCsvExport = () => {
    if (customExportCsv) {
      customExportCsv(table)
    } else {
      exportTableToCsv(workbookTitle, table, config, generateFileNameWithDate(workbookTitle, 'csv'))
    }
    closeMenu()
  }

  return (
    <div>
      {/* Alert forces .MuiButton-root to use its color with !important, so we need to manually set the text and icon color here. */}
      <Button
        variant='text'
        onClick={openMenu}
        endIcon={<ChevronDownIcon sx={{ color: theme.palette.text.primary }} />}
        color='inherit'
        sx={{
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <span style={{ color: theme.palette.text.primary }}>{t('export', 'Export')}</span>
      </Button>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleXlsxExport()
          }}
        >
          Microsoft Excel (.xlsx)
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCsvExport()
          }}
        >
          {t('commaSeparatedValues', 'Comma-separated values')} (.csv)
        </MenuItem>
      </Menu>
    </div>
  )
}
